import { createContext, useEffect, useReducer, useState } from "react"
import { useDebouncedState } from "../../hooks/useDebouncedState"
import { UserDto } from "./usersApi"
import { hasUserChanged, isValidEmail } from "./usersModel"
import { useUserQuery, useValidateUserEmailQuery } from "./usersQueries"
import { emptyUserState, UserFormAction, userFormReducer } from "./usersReducer"

type UsersPageContextData = {
  state: UserDto
  dispatch: (action: UserFormAction) => void
  isLoading: boolean
  isNewUser: boolean
  isDeletedUser: boolean,
  isEmailAvailable: boolean
  isEmailValid: boolean
  userHasChanges?: boolean
  isEmailValidationQueryLoading?: boolean
  selectedUserEmail?: string
}

const UsersPageContext = createContext<UsersPageContextData>({
  state: emptyUserState(),
  dispatch: () => { },
  isLoading: false,
  isNewUser: false,
  isDeletedUser: false,
  isEmailAvailable: true,
  isEmailValid: true,
  userHasChanges: false,
  isEmailValidationQueryLoading: false,
  selectedUserEmail: ''
})

export default UsersPageContext

export type Props = React.PropsWithChildren & {
  userEmail: string
  showDeletedView: boolean
}

export const UsersPageContextWrapper = ({ userEmail, showDeletedView, children }: Props) => {
  const [state, dispatch] = useReducer(userFormReducer, emptyUserState())
  const [serverState, setServerState] = useState<UserDto | undefined>(undefined)
  const { debouncedState: debouncedEmail, setState: setDebouncedEmail } = useDebouncedState(state.email, 300)
  const isEmailValid = isValidEmail(state.email)
  const userHasChanges = hasUserChanged(state, serverState!)
  const isNewUser = !userEmail

  const validateUserEmailQuery = useValidateUserEmailQuery(debouncedEmail, isNewUser)
  const isEmailAvailable = validateUserEmailQuery.data || false

  const userQuery = useUserQuery(userEmail, showDeletedView)

  useEffect(() => {
    if(userEmail === ''){
      dispatch({ type: 'RESET_FORM', payload: emptyUserState() })
      return
    }

    if (!userQuery.data) {
      return
    }

    dispatch({ type: 'RESET_FORM', payload: userQuery.data })
    setServerState(userQuery.data)
  }, [userEmail, userQuery.data])

  useEffect(() => {
    setDebouncedEmail(state.email)
  }, [state.email])

  return <UsersPageContext.Provider
    value={{
      state,
      dispatch,
      isLoading: userQuery.isLoading,
      isNewUser,
      isDeletedUser: showDeletedView,
      isEmailAvailable,
      isEmailValid,
      userHasChanges,
      isEmailValidationQueryLoading: validateUserEmailQuery.isLoading,
      selectedUserEmail: userEmail
    }}>
    { children }
  </UsersPageContext.Provider>
}